import { useMemo } from "react";
import { IconTrash } from "@tabler/icons-react";
import { IconButton, Input, Stack, Switch, Text, Textarea } from "@/components/ui";
import type { EstadoDisenador } from "../../../hooks/use-estado-disenador";
import type { CatalogoNotificaciones, Notificacion } from "../../../services/notificaciones-service";
import { ETIQUETA_CANAL, eventosConectados, variablesDisponibles } from "../../../utils/lienzo";
import { ContenidoCanal } from "./ContenidoCanal";
import { DestinatariosEditor } from "./DestinatariosEditor";
import { EncabezadoPanel } from "./PanelPropiedades";
import { VistaPreviaYPrueba } from "./VistaPreviaYPrueba";

interface Props {
  flujoId: string;
  notificacion: Notificacion;
  disenador: EstadoDisenador;
  catalogo: CatalogoNotificaciones;
}

/** Notificación seleccionada: datos generales, destinatarios, contenido, vista previa y prueba. */
export function PanelNotificacion({ flujoId, notificacion: n, disenador: d, catalogo }: Props) {
  const cambiar = (cambio: Partial<Notificacion>) => d.actualizarNotificacion(n.clave, cambio);
  const eventos = useMemo(() => eventosConectados(n.clave, d.estado, catalogo.eventos), [n.clave, d.estado, catalogo.eventos]);
  const variables = useMemo(() => variablesDisponibles(eventos), [eventos]);

  return (
    <div>
      <EncabezadoPanel
        titulo={n.nombre || "Notificación"}
        subtitulo={ETIQUETA_CANAL[n.canal]}
        acciones={
          <IconButton size="sm" aria-label="Quitar notificación" title="Quitar notificación" onClick={() => d.quitar(n.clave)}>
            <IconTrash />
          </IconButton>
        }
      />
      <Stack gap="lg" className="p-4">
        <Seccion titulo="General">
          <Input label="Nombre" size="xs" maxLength={150} value={n.nombre} onChange={(e) => cambiar({ nombre: e.target.value })} />
          <Textarea label="Descripción" size="xs" rows={2} maxLength={1000} value={n.descripcion ?? ""} onChange={(e) => cambiar({ descripcion: e.target.value || null })} />
          <Switch label="Activa" checked={n.activa} onCheckedChange={(activa) => cambiar({ activa })} />
          {n.canal !== "Webhook" ? (
            <Switch
              label="Obligatoria"
              description="Los usuarios no pueden silenciarla desde su perfil."
              checked={n.obligatoria}
              onCheckedChange={(obligatoria) => cambiar({ obligatoria })}
            />
          ) : null}
        </Seccion>

        {n.canal !== "Webhook" ? (
          <Seccion titulo="Destinatarios">
            <DestinatariosEditor canal={n.canal} destinatarios={n.destinatarios} onCambio={(destinatarios) => cambiar({ destinatarios })} />
          </Seccion>
        ) : null}

        <Seccion titulo="Contenido">
          {eventos.length === 0 ? (
            <Text size="xs" c="dimmed">Conecta un evento para usar sus variables en la plantilla.</Text>
          ) : (
            <Text size="xs" c="dimmed">
              Variables de: {eventos.map((e) => e.nombre).join(", ")}. Escribe <span className="font-mono">{"{{"}</span> para sugerencias;
              formatos con <span className="font-mono">|</span> ({catalogo.formatos.join(", ")}).
            </Text>
          )}
          <ContenidoCanal notificacion={n} variables={variables} onCambio={cambiar} />
        </Seccion>

        <Seccion titulo="Vista previa y prueba">
          <VistaPreviaYPrueba flujoId={flujoId} notificacion={n} eventos={eventos} disenador={d} />
        </Seccion>

        <Text size="xs" c="dimmed" ff="mono">{n.clave}</Text>
      </Stack>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2.5">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">{titulo}</Text>
      {children}
    </section>
  );
}
